import PageBackBar from "@/components/PageBackBar";
import ReviewCarousel from "@/components/ReviewCarousel";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { useSite } from "@/Layout";
import { trackEvent } from "@/hooks/useBusinessData";

export default function ReviewsPage() {
  const { reviews, ratingMeta } = useSite();
  const yelpUrl =
    ratingMeta.url || "https://m.yelp.com/biz/shecuts-barber-studio-atlanta";

  return (
    <div data-testid="reviews-page">
      <PageBackBar testId="reviews-back-bar" />
      <ReviewCarousel reviews={reviews} ratingMeta={ratingMeta} />

      {/* Full review grid — every published Yelp review, scannable */}
      <section className="bg-black border-t border-white/10 py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="eyebrow mb-8">§ Every review</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {reviews.map((r, i) => (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{
                  duration: 0.55,
                  delay: (i % 4) * 0.05,
                  ease: [0.7, 0, 0.2, 1],
                }}
                className="border border-white/10 p-6 md:p-8 flex flex-col justify-between hover:bg-white/[0.02] transition-colors"
                data-testid={`review-card-${r.id}`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex">
                      {[...Array(r.rating)].map((_, k) => (
                        <Star
                          key={k}
                          className="w-3.5 h-3.5 fill-current"
                          style={{ color: "var(--sc-star)" }}
                        />
                      ))}
                    </div>
                    <div className="eyebrow text-zinc-500">
                      {r.source_label || "Yelp"}
                    </div>
                  </div>
                  <p className="font-serif text-lg md:text-xl leading-snug mt-5">
                    “{r.text || r.review_text}”
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <div className="font-serif text-base">
                      {r.reviewer_name || r.name}
                    </div>
                    <div className="eyebrow text-zinc-500 mt-1 text-[9px]">
                      {r.location || "Atlanta, GA"}
                    </div>
                  </div>
                  <a
                    href={r.source_url || yelpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("yelp_reviews_clicked", {
                        source: "reviews_grid",
                        review_id: r.id,
                      })
                    }
                    className="eyebrow inline-flex items-center gap-1.5 line-hover"
                    data-testid={`review-grid-yelp-${r.id}`}
                  >
                    View on Yelp
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
